import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'
import { humidorApi } from './humidorApi'

export interface Cigar {
  id: string
  brandId: string
  cigarLineId: string
  humidorId: string
  vitolaId: string
  quantity: number
  length: number
  ringGauge: number
  purchasePrice?: number | string
  purchaseDate?: string
  purchaseLocation?: string
  notes?: string
  status: 'available' | 'smoked' | 'traded' | 'gifted' | 'damaged'
  imageUrls: string[]
  batchNumber?: string
  wrapperType?: string
  binderType?: string
  fillerType?: string
  strengthLevel?: string
  createdAt: string
  updatedAt: string
  brand: {
    id: string
    name: string
    country?: string
  }
  cigarLine: {
    id: string
    name: string
  }
  humidor: {
    id: string
    name: string
  }
  vitola: {
    id: string
    name: string
    description?: string
  }
}

export interface CigarLine {
  id: string
  brandId: string
  name: string
  description?: string
  strengthLevel?: string
  wrapperType?: string
  binderType?: string
  fillerType?: string
  createdAt: string
  updatedAt: string
  brand: {
    id: string
    name: string
    country?: string
  }
}

export interface CigarMovement {
  id: string
  cigarId: string
  userId: string
  sourceHumidorId: string
  destinationHumidorId: string
  quantityMoved: number
  notes?: string
  movedAt: string
}

export interface Brand {
  id: string
  name: string
  country?: string
  description?: string
  website?: string
  logoUrl?: string
  foundedYear?: number
  createdAt: string
  updatedAt: string
}

export interface CreateCigarRequest {
  brandId: string
  cigarLineId: string
  humidorId: string
  vitolaId: string
  quantity?: number
  length?: number
  ringGauge?: number
  purchasePrice?: number
  purchaseDate?: string
  purchaseLocation?: string
  notes?: string
  imageUrls?: string[]
  batchNumber?: string
  status?: 'available' | 'smoked' | 'traded' | 'gifted' | 'damaged'
}

export interface UpdateCigarRequest {
  vitolaId?: string
  quantity?: number
  length?: number
  ringGauge?: number
  purchasePrice?: number
  purchaseDate?: string
  purchaseLocation?: string
  notes?: string
  status?: 'available' | 'smoked' | 'traded' | 'gifted' | 'damaged'
  imageUrls?: string[]
  batchNumber?: string
}

export interface CigarSearchParams {
  search?: string
  brandId?: string
  cigarLineId?: string
  humidorId?: string
  vitolaId?: string
  status?: string
  wrapperType?: string
  strengthLevel?: string
  origin?: string
  purchaseDateStart?: string
  purchaseDateEnd?: string
  minPrice?: number
  maxPrice?: number
  purchaseDateFrom?: string
  purchaseDateTo?: string
  sortBy?: 'name' | 'brand' | 'purchaseDate' | 'purchasePrice' | 'createdAt' | 'quantity'
  sortOrder?: 'asc' | 'desc'
  page?: number
  limit?: number
}

export interface CigarSearchResponse {
  cigars: Cigar[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface CigarStats {
  totalCount: number
  availableCount: number
  totalValue: number
  averageValue: number
  uniqueBrands: number
}

export interface BulkMoveCigarsRequest {
  cigarIds: string[]
  destinationHumidorId: string
  notes?: string
}

export interface BulkMoveCigarsResult {
  movedCount: number
  skippedCount: number
  message: string
  destinationHumidor: { id: string; name: string }
}

export const cigarApi = createApi({
  reducerPath: 'cigarApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/cigars',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Cigar', 'CigarLine', 'Brand'],
  endpoints: (builder) => ({
    getCigars: builder.query<CigarSearchResponse, CigarSearchParams>({
      query: (params) => ({
        url: '',
        params,
      }),
      transformResponse: (response: { success: boolean; data: { cigars: Cigar[]; pagination: { page: number; limit: number; totalCount: number; totalPages: number } } }) => ({
        cigars: response.data.cigars,
        total: response.data.pagination.totalCount,
        page: response.data.pagination.page,
        limit: response.data.pagination.limit,
        totalPages: response.data.pagination.totalPages,
      }),
      providesTags: ['Cigar'],
    }),
    getCigar: builder.query<Cigar, string>({
      query: (id) => `/${id}`,
      transformResponse: (response: { success: boolean; data: { cigar: Cigar } }) => response.data.cigar,
      providesTags: (_result, _error, id) => [{ type: 'Cigar', id }],
    }),
    createCigar: builder.mutation<Cigar, CreateCigarRequest>({
      query: (cigar) => ({
        url: '',
        method: 'POST',
        body: cigar,
      }),
      transformResponse: (response: { success: boolean; data: { cigar: Cigar } }) => response.data.cigar,
      invalidatesTags: ['Cigar'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(humidorApi.util.invalidateTags(['Humidor']))
        } catch { /* mutation failed */ }
      },
    }),
    updateCigar: builder.mutation<Cigar, { id: string; updates: UpdateCigarRequest }>({
      query: ({ id, updates }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: updates,
      }),
      transformResponse: (response: { success: boolean; data: { cigar: Cigar } }) => response.data.cigar,
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Cigar', id }],
    }),
    deleteCigar: builder.mutation<void, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Cigar'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(humidorApi.util.invalidateTags(['Humidor']))
        } catch { /* mutation failed */ }
      },
    }),
    moveCigar: builder.mutation<Cigar, { id: string; destinationHumidorId: string; quantity?: number }>({
      query: ({ id, destinationHumidorId, quantity }) => ({
        url: `/${id}/move`,
        method: 'PATCH',
        body: { destinationHumidorId, quantity },
      }),
      transformResponse: (response: { success: boolean; data: Cigar }) => response.data,
      invalidatesTags: ['Cigar'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(humidorApi.util.invalidateTags(['Humidor']))
        } catch {
          // Move failed, no need to invalidate humidor cache
        }
      },
    }),
    getCigarMovements: builder.query<CigarMovement[], string>({
      query: (id) => `/${id}/movements`,
      transformResponse: (response: { success: boolean; data: { movements: CigarMovement[] } }) =>
        response.data.movements,
    }),
    bulkMoveCigars: builder.mutation<BulkMoveCigarsResult, BulkMoveCigarsRequest>({
      query: (request) => ({
        url: '/bulk-move',
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: { success: boolean; message: string; data: BulkMoveCigarsResult }) => response.data,
      invalidatesTags: ['Cigar'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(humidorApi.util.invalidateTags(['Humidor']))
        } catch {
          // Bulk move failed
        }
      },
    }),
    getCigarLines: builder.query<CigarLine[], { search?: string; brandId?: string }>({
      query: (params) => ({
        url: '/cigar-lines',
        params,
      }),
      transformResponse: (response: { success: boolean; data: { cigarLines: CigarLine[] } }) => response.data.cigarLines,
      providesTags: ['CigarLine'],
    }),
    getCigarLine: builder.query<CigarLine, string>({
      query: (id) => `/cigar-lines/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'CigarLine', id }],
    }),
    getBrands: builder.query<Brand[], { search?: string }>({
      query: (params) => ({
        url: '/brands',
        params,
      }),
      transformResponse: (response: { success: boolean; data: { brands: Brand[] } }) => response.data.brands,
      providesTags: ['Brand'],
    }),
    getBrand: builder.query<Brand, string>({
      query: (id) => `/brands/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Brand', id }],
    }),
    searchCigars: builder.query<CigarSearchResponse, CigarSearchParams>({
      query: (params) => ({
        url: '/search',
        params,
      }),
      transformResponse: (response: {
        success: boolean
        data: {
          cigars: Cigar[]
          pagination: {
            page: number
            limit: number
            totalCount: number
            totalPages: number
          }
        }
      }): CigarSearchResponse => ({
        cigars: response.data.cigars,
        total: response.data.pagination.totalCount,
        page: response.data.pagination.page,
        limit: response.data.pagination.limit,
        totalPages: response.data.pagination.totalPages,
      }),
      providesTags: ['Cigar'],
    }),
    getCigarStats: builder.query<CigarStats, void>({
      query: () => '/stats',
      transformResponse: (response: { success: boolean; data: CigarStats }) => response.data,
      providesTags: ['Cigar'],
    }),
  }),
})

export const {
  useGetCigarsQuery,
  useGetCigarQuery,
  useCreateCigarMutation,
  useUpdateCigarMutation,
  useDeleteCigarMutation,
  useMoveCigarMutation,
  useGetCigarMovementsQuery,
  useBulkMoveCigarsMutation,
  useGetCigarLinesQuery,
  useGetCigarLineQuery,
  useGetBrandsQuery,
  useGetBrandQuery,
  useSearchCigarsQuery,
  useLazySearchCigarsQuery,
  useGetCigarStatsQuery,
} = cigarApi
